import React, { useState, useEffect } from 'react'
import { Tabs, Tab } from 'components/shared/Tabs'
import EntityInfo from './Summaries/Components/EntityInfo/EntityInfo'
import MenuOptions from './Summaries/Components/Overview/MenuOptions'
import AuthService from 'tools/AuthService'
import AddDebtorAlert from './Summaries/Components/Overview/AddDebtorAlert'
import { getFormModePayload } from 'constants/utilityFunctions'
import BusinessModal from 'components/Applicants/partial/Dialog/BusinessModal'
import OwnerModal from 'components/Applicants/partial/Dialog/OwnerModal'
import strings from 'strings'
import './Summary.scss'
import {
    dispatchFormMode,
    createOwner,
    updateOwner,
    updateDebtor,
    updateBusiness,
    getApplicantsById,
    requestApplicationInfo
} from 'actions/applicants'

const Overview = (props) => {

    const {
        applicantId,
        formMode,
        modalMode,
        businessData,
        debtorData,
        hideFmcsa,
        getSalesRepresentatives,
        // redux form props
        reset,
        dispatch,
        handleSubmit,
        dirty,
        change,
        isBrokerCustomer,
        requestApplicationInfoFn = null // Optional marketplace function override
    } = props

    const [applicationInfo, setApplicationInfo] = useState({})
    const [tabValue, setTabValue] = useState(0)
    const [modal, setModal] = useState({ business: false, owner: false, debtor: false })
    const [loading, setLoading] = useState(false)

    const business = applicationInfo.business || false
    const owners = applicationInfo.owners || []
    const debtor = applicationInfo.debtor || false

    // Build dynamic tabs based on available data
    const getFilteredTabs = () => {
        const tabs = [{ label: 'Business Information' }]
        if (owners && owners.length > 0) {
            tabs.push({ label: 'Owners' })
        }
        tabs.push({ label: 'Debtor' })
        return tabs
    }

    const filteredTabs = getFilteredTabs()

    // Get type based on dynamic tab structure
    const getType = () => {
        if (tabValue === 0) {
            return 'business'
        }
        if (owners && owners.length > 0) {
            if (tabValue === 1) return 'owner'
            if (tabValue === 2) return 'debtor'
        } else {
            if (tabValue === 1) return 'debtor'
        }
        return 'business'
    }

    const type = getType()

    // useEffect to get application info
    useEffect(() => {
        if (applicantId) {
            getApplicationInfo();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [applicantId]);

    // get application info
    const getApplicationInfo = () => {
        const requestInfoFunction = requestApplicationInfoFn || requestApplicationInfo;

        if (typeof requestInfoFunction !== 'function') {
            console.error('requestInfoFunction is not a function:', typeof requestInfoFunction);
            return;
        }

        const apiCall = requestInfoFunction(applicantId);
        if (typeof apiCall !== 'function') {
            console.error('requestInfoFunction did not return a function:', typeof apiCall);
            return;
        }

        apiCall()
            .then((res) => {
                setApplicationInfo(res);
            })
            .catch((error) => {
                setApplicationInfo({
                    business: false,
                    debtor: false,
                    owners: []
                });
            });
    }

    // function to handle edit/create click
    const dialogControl = (mode, uuid = false) => {
        reset('editForm')
        const formModePayload = getFormModePayload(mode, type, uuid)
        dispatch(dispatchFormMode(formModePayload))
        setModal({ ...modal, [type]: true })
    }

    // fuction to edit business
    const editBusiness = (formData) => {
        // if loading is true, means already a request is in process
        if (loading) return
        setLoading(true)
        if (typeof formData?.contractAmount === "string") {
            formData.contractAmount = Number(formData.contractAmount.split(",").join(""))
        }
        if (formData.hasOwnProperty('identifications') && Array.isArray(formData.identifications)
            && formData.identifications.length > 0) {
            if (typeof formData?.identifications[0].identification === "string") {
                if (formData.identifications[0].identification.length > 0) {
                    formData.identifications[0].identificationType = 'EIN'
                    formData.identifications[0].primary = true
                } else {
                    delete formData.identifications;
                }
            }
        }

        updateBusiness(applicantId, formData)()
            .then(() => {
                getApplicationInfo()
                getApplicantsById(applicantId)(dispatch)
            }).finally(() => {
            setModal({ business: false, owner: false, debtor: false })
            setTimeout(() => { setLoading(false) }, 1000)
        })
    }

    // onClose business, owner or debtor modal
    const onCloseModal = () => {
        setModal({ business: false, owner: false, debtor: false })
        dispatch(dispatchFormMode({
            mode: 'Default',
            type: ''
        }))
    }

    // function to edit debtor
    const editDebtor = (formData) => {
        // if loading is true, means already a request is in process
        if (loading) return
        setLoading(true)
        const requestType = formMode.mode === 'Create' ? 'POST' : 'PATCH'

        if (!formData.emails || !formData.emails[0]?.email) {
            formData.emails = null
        } else {
            formData.emails[0].emailType = 'Work'
            formData.emails[0].primary = true
        }

        if (!formData.phones[0] || !formData.phones[0]?.number) {
            formData.phones = null;
        } else {
            formData.phones[0].phoneType = 'Work';
            formData.phones[0].primary = true;
        }

        if (!formData.identifications || !formData.identifications[0]?.identification) {
            formData.identifications = null
        } else {
            formData.identifications[0].identificationType = 'EIN'
        }

        updateDebtor(applicantId, formData, requestType)()
            .then(() => {
                getApplicationInfo()
                getApplicantsById(applicantId)(dispatch)
            }).finally(() => {
            setModal({ business: false, owner: false, debtor: false })
            setTimeout(() => { setLoading(false) }, 1000)
        })
    }

    // function to submit owner
    const ownerSubmit = (formData) => {
        const callApi = formMode.mode === 'Create' ? createOwner : updateOwner

        if (!formData?.identifications[0]?.identification)
            delete formData.identifications

        if (formMode.mode === "Edit" && formData?.identifications?.length)
            formData.identifications = formData.identifications.map(data => ({
                ...data,
                identificationType: 'SSN',
                'primary': true
            }))

        callApi(applicantId, formData)()
            .then(() => {
                getApplicationInfo()
                getApplicantsById(applicantId)(dispatch)
            }).finally(() => {
            setModal({ business: false, owner: false, debtor: false })
        })
    }

    const headerEntity = type === 'business' ? business : (type === 'debtor' ? debtor : null)
    const showHeaderMenu = !!headerEntity && !AuthService.isReadOnlyUser() && !isBrokerCustomer

    return (
        <div className='m-3'>
            <div className='overviewHeader'>
                <h5 className='mb-0'> {strings.APPLICANT.SUMMARY.OVERVIEW} </h5>
                {showHeaderMenu &&
                    <MenuOptions
                        entityType={type}
                        hideAdd
                        dialogControl={dialogControl}
                        uuid={headerEntity.uuid}
                    />
                }
            </div>
            <Tabs
                data={filteredTabs}
                value={tabValue}
                onChange={(e, value) => setTabValue(value)}
                className='mb-5'
            />
            <Tab value={tabValue} index={0}>
                {business && <EntityInfo isBrokerCustomer={isBrokerCustomer} entity={business} entityType='business' dialogControl={dialogControl} hideMenu />}
            </Tab>
            {owners && owners.length > 0 && (
                <Tab value={tabValue} index={1}>
                    {
                        owners.map((owner, index) => {
                            return (<div key={owner.uuid}>
                                {index > 0 && <hr />}
                                <EntityInfo isBrokerCustomer={isBrokerCustomer} entity={owner} entityType='owner' disableAdd={owners.length > 4} dialogControl={dialogControl} />
                            </div>)
                        })
                    }
                </Tab>
            )}
            {debtor && (
                <Tab value={tabValue} index={owners && owners.length > 0 ? 2 : 1}>
                    <EntityInfo isBrokerCustomer={isBrokerCustomer} entity={debtor} entityType='debtor' dialogControl={dialogControl} hideMenu />
                </Tab>
            )}
            {!debtor && (
                <Tab value={tabValue} index={owners && owners.length > 0 ? 2 : 1}>
                    <AddDebtorAlert isBrokerCustomer={isBrokerCustomer} dialogControl={dialogControl} />
                </Tab>
            )}

            {/* Modals to edit business, owner and debtor */}

            <BusinessModal
                open={modal.business || modal.debtor}
                onClose={onCloseModal}
                onSubmit={handleSubmit(formData => type === 'debtor' ? editDebtor(formData) : editBusiness(formData))}
                dirty={dirty}
                change={change}
                type={type}
                modalMode={modalMode}
                getSalesRepresentatives={getSalesRepresentatives}
                hideFmcsa={hideFmcsa}
                addresses={type === 'debtor' ? debtorData?.addresses : businessData?.addresses}
                loading={loading}
            />

            {owners && owners.length > 0 && (
                <OwnerModal
                    open={modal.owner}
                    onClose={onCloseModal}
                    onSubmit={handleSubmit(formData => ownerSubmit(formData))}
                    modalMode={modalMode}
                    change={change}
                    dirty={dirty}
                    loading={loading}
                />
            )}

        </div>
    )
}

export default Overview